import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useRouteMatch } from "react-router";
import { useHistory } from "react-router-dom";
import AceEditor from "react-ace";
import { KTextInput } from "@kyburz/kdesign-system";
import useSetState from "../../hooks/useSafeSetState";
import BaseLayout from "../../components/layouts/BaseLayout";
import KFormBackground from "../../components/ui/KFormBackground";
import {
  getTransformation,
  editTransformation,
  createTransformation,
} from "../../apiCalls/config/transformations";
import { setAlert } from "../../redux/actions/actions";

import { useStyles } from "./styles";

interface Props {
  edit: boolean;
}

const TransformationView = ({ edit }: Props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history: any = useHistory();
  const match = useRouteMatch({
    path: "/config/templates/transformations/:templateName",
    strict: true,
    sensitive: true,
  });
  const { templateName } = match?.params as any;

  const [
    { name, tags, transformation, errorMessage, nameError, nameErrorText },
    setState,
  ] = useSetState({
    name: "",
    tags: "{}",
    transformation: "",
    errorMessage: null,
    nameError: false,
    nameErrorText: "",
  });

  const handleChange = (e) => setState({ [e.target.name]: e.target.value });

  const handleTransformationChange = (newTransformation) => {
    let errorM = null;
    try {
      JSON.parse(newTransformation);
    } catch (e) {
      errorM = e.message;
    }
    setState({
      transformation: newTransformation,
      errorMessage: errorM,
    });
  };

  const handleSubmit = () => {
    if (edit) {
      editTransformation(name, tags, transformation)
        .then(() => {
          dispatch(setAlert("Saved successfully", "success"));
        })
        .catch((e) => {
          dispatch(setAlert("An error occurred", "error"));
          console.error(e);
        });
    } else {
      createTransformation(name, tags, transformation)
        .then(() => {
          history.push("/config/templates/transformations");
          dispatch(setAlert("Saved successfully", "success"));
        })
        .catch((e) => {
          dispatch(setAlert("An error occurred", "error"));
          console.error(e);
        });
    }
  };

  useEffect(() => {
    if (edit) {
      getTransformation(templateName).then((res) => {
        const { data } = res;
        setState({
          name: data.name,
          transformation:
            data.transformation === null
              ? ""
              : JSON.stringify(res.data.transformation, null, 2),
        });
      });
    }
  }, []);

  useEffect(() => {
    if (name.includes(" ")) {
      setState({
        nameError: true,
        nameErrorText: "No spaces allowed in the name",
      });
    } else {
      setState({ nameError: false, nameErrorText: "" });
    }
  }, [name]);

  return (
    <BaseLayout
      title={
        edit ? `Edit Transformation ${templateName}` : "Create Transformation"
      }
      handleArrowClick={() => history.push("/config/templates/transformations")}
    >
      <KFormBackground
        size="large"
        primaryCTAColor="primary"
        primaryCTAonClick={handleSubmit}
        primaryCTAText={edit ? "Update" : "Create"}
        secondaryCTAText="Cancel"
        secondaryCTAonClick={() => history.goBack()}
        disabled={!!errorMessage || !name || !transformation || nameError}
      >
        <div className={classes.inputContainer}>
          <KTextInput
            required
            placeholder="Transformation Name"
            label="Name"
            name="name"
            value={name}
            onChange={handleChange}
            disabled={edit}
            style={{ display: "inline-block", marginRight: "24px" }}
            error={nameError}
            errorText={nameErrorText}
            helperText="No Spaces allowed"
          />
          <KTextInput
            label="Tags"
            name="tags"
            value={tags}
            onChange={handleChange}
            disabled
            width="320px"
            style={{ display: "inline-block" }}
          />
        </div>

        <div className={classes.transformationContainer}>
          <p className={classes.transformationHeader}>Transformation</p>
          <AceEditor
            mode="json"
            width="752px"
            height="500px"
            theme="monokai"
            name={name}
            onChange={handleTransformationChange}
            fontSize={14}
            showPrintMargin={true}
            showGutter={true}
            highlightActiveLine={true}
            value={transformation}
            setOptions={{
              enableBasicAutocompletion: false,
              enableLiveAutocompletion: false,
              enableSnippets: false,
              showLineNumbers: true,
              tabSize: 2,
              useWorker: false,
            }}
            style={{ borderRadius: 4 }}
          />
          {errorMessage && (
            <p className={classes.transformationError}>
              {`Validation error: ${errorMessage}`}
            </p>
          )}
        </div>
      </KFormBackground>
    </BaseLayout>
  );
};

export default TransformationView;
